import { Button, Strong } from '@radix-ui/themes';
import { CustomIcon } from 'components/common/custom-icon';
import { DialogButton } from 'components/common/dialogs/button';
import { CommonConfirmationDialog } from 'components/common/dialogs/confirmation';
import { ErrorBoundary } from 'components/common/error-boundary';
import { MachineContext } from 'contexts/machine.context';
import { isFuture } from 'date-fns';
import { CustomIconPath } from 'enums/custom.enums';
import useAutoFireSafetyListener from 'hooks/machine/useAutoFireSafetyListener';
import { t } from 'i18next';
import { QUEUE_STATES_THAT_ALLOW_AUTO_FIRE } from 'lib_ts/enums/machine-msg.enum';
import { MACHINE_STATUSES_THAT_ALLOW_AUTO_FIRE } from 'lib_ts/enums/machine.enums';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { useContext, useMemo, useState } from 'react';
import useMachineStatusStore from 'stores/use-machine-status-store';

const CONTEXT_NAME = 'MachineAutoFireButton';

interface IProps {
  className?: string;
  // i.e. confirm before enabling auto-fire (e.g. for non-SA)
  confirm?: boolean;
  as?: 'dialog-button';

  // i.e. value after toggling
  beforeToggleFn?: (newValue: boolean) => void;
}

export const MachineAutoFireButton = (props: IProps) => {
  useAutoFireSafetyListener();

  const { autoFire, setAutoFire } = useContext(MachineContext);
  const queueState = useMachineStatusStore((s) => s.queueState);
  const r2fMachineStatus = useMachineStatusStore(
    (s) => s.r2fData?.machine_status
  );
  const fireStateDates = useMachineStatusStore((s) => s.fireStateDates);

  const [confirmed, setConfirmed] = useState(false);
  const [dialogConfirm, setDialogConfirm] = useState<number>();

  const disabled = useMemo(() => {
    if (autoFire) {
      // users should always be allowed to turn off auto-fire
      console.debug(`${CONTEXT_NAME}: auto-fire is enabled > allow disable`);
      return false;
    }

    if (!autoFire && isFuture(fireStateDates.awaitFiringUntil)) {
      // users cannot enable auto-fire while waiting on a fire response
      console.debug(
        `${CONTEXT_NAME}: auto-fire cannot be enabled while waiting for fire response`
      );
      return true;
    }

    if (!QUEUE_STATES_THAT_ALLOW_AUTO_FIRE.includes(queueState)) {
      // only active users can toggle auto-fire
      console.debug(
        `${CONTEXT_NAME}: auto-fire cannot be changed while in inactive queue state`
      );
      return true;
    }

    if (
      !r2fMachineStatus ||
      !MACHINE_STATUSES_THAT_ALLOW_AUTO_FIRE.includes(r2fMachineStatus)
    ) {
      // "bad" machine statuses should block auto-fire changes
      console.debug(
        `${CONTEXT_NAME}: auto-fire cannot be changed while in ${r2fMachineStatus} machine status`
      );
      return true;
    }

    // default
    return false;
  }, [autoFire, queueState, r2fMachineStatus, fireStateDates.awaitFiringUntil]);

  const button = useMemo(() => {
    const onClick = () => {
      if (disabled) {
        // shouldn't trigger since a disabled button would not fire the click handler
        return;
      }

      const nextValue = !autoFire;

      if (props.confirm && nextValue && !confirmed) {
        setDialogConfirm(Date.now());
        return;
      }

      props.beforeToggleFn?.(nextValue);

      setAutoFire(nextValue);
    };

    switch (props.as) {
      case 'dialog-button': {
        return (
          <DialogButton
            className={props.className}
            color={autoFire ? RADIX.COLOR.WARNING : RADIX.COLOR.NEUTRAL}
            variant="soft"
            disabled={disabled}
            onClick={onClick}
            label="common.auto"
            suffixIcon={
              <CustomIcon
                icon={
                  autoFire ? CustomIconPath.SwitchOn : CustomIconPath.SwitchOff
                }
              />
            }
          />
        );
      }

      default: {
        return (
          <Button
            size={RADIX.BUTTON.SIZE.SM}
            className={props.className}
            color={autoFire ? RADIX.COLOR.WARNING : RADIX.COLOR.NEUTRAL}
            variant="soft"
            disabled={disabled}
            onClick={onClick}
          >
            {t('common.auto')}
            &nbsp;
            <CustomIcon
              icon={
                autoFire ? CustomIconPath.SwitchOn : CustomIconPath.SwitchOff
              }
            />
          </Button>
        );
      }
    }
  }, [props.className, props.as, autoFire, disabled]);

  return (
    <ErrorBoundary identifier="MachineAutoFireButton">
      {button}
      {dialogConfirm && (
        <CommonConfirmationDialog
          key={dialogConfirm}
          identifier="MachineAutoFireButtonConfirm"
          title="common.enable-auto-firing"
          content={
            <p>
              You are about to enable <Strong>Auto Firing</Strong>. Please
              ensure the following:
              <ol>
                <li>Wear a helmet while hitting off Arc.</li>
                <li>Do not leave the machine or app unattended.</li>
                <li>
                  None of the pitches in your queue are aimed inside toward the
                  batter.
                </li>
              </ol>
              Be aware that misuse can result in serious injury.{' '}
              <Strong>Are you sure you want to enable Auto Firing?</Strong>
            </p>
          }
          action={{
            label: 'common.enable-auto-firing',
            onClick: async () => {
              setConfirmed(true);
              setDialogConfirm(undefined);

              props.beforeToggleFn?.(true);

              setAutoFire(true);
            },
          }}
        />
      )}
    </ErrorBoundary>
  );
};
